import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Key, Shield, MoreVertical, Play, Edit, Trash2 } from 'lucide-react';
import { useApi } from '../hooks/useUser.jsx';

/**
 * Configuration List Page
 * 
 * Display and manage SAML/OIDC configurations - styled to match main IDNTY website
 * Each user only sees their own configurations (multi-user isolation)
 */
export function ConfigList() {
  const [configs, setConfigs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const { fetchWithUser, userId } = useApi();
  
  React.useEffect(() => {
    loadConfigs();
  }, [userId]);
  
  const loadConfigs = async () => {
    if (!userId) return; // Wait for user ID
    
    try {
      setLoading(true);
      const response = await fetchWithUser('/api/configs');
      if (!response.ok) throw new Error('Failed to load configurations');
      const data = await response.json();
      setConfigs(data.configs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;
    
    try {
      const response = await fetchWithUser(`/api/configs/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete configuration');
      setConfigs(configs.filter(c => c.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };
  
  return (
    <div className="animate-fade-in">
      {/* Header Section */}
      <div className="bg-white border-b border-border mb-8 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#333333]">
              <span className="text-[#D4AF37]">Configurations</span>
            </h1>
            <p className="text-[#666666] mt-1 text-lg">
              Manage your SAML and OIDC test configurations
            </p>
          </div>
          <Link to="/configs/new" className="bg-[#D4AF37] text-white px-8 py-4 rounded-md font-bold text-lg hover:bg-[#B8962E] transition-colors flex items-center">
            <Plus className="h-5 w-5 mr-2" />
            New Configuration
          </Link>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="card p-6 text-center text-destructive border-destructive">
            {error}
          </div>
        ) : configs.length === 0 ? (
          <div className="card-heavy p-12 text-center bg-white">
            <Shield className="h-12 w-12 mx-auto text-primary mb-4" />
            <h3 className="font-semibold mb-2 text-lg">No configurations yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Create your first SAML or OIDC configuration to start testing your Identity Provider integration.
            </p>
            <Link to="/configs/new" className="btn btn-gold">
              <Plus className="h-4 w-4 mr-2" />
              Create Configuration
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {configs.map((config) => (
              <ConfigCard
                key={config.id}
                config={config}
                onDelete={() => handleDelete(config.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfigCard({ config, onDelete }) {
  const [showMenu, setShowMenu] = React.useState(false);
  
  return (
    <div className="card-heavy p-8 bg-white border-2 border-[#EEEEEE] hover:border-[#D4AF37] transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-6">
          <div className={`p-4 rounded-lg ${
            config.type === 'saml' 
              ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
              : 'bg-[#D4AF37]/10 text-[#D4AF37]'
          }`}>
            {config.type === 'saml' ? (
              <Key className="h-6 w-6" />
            ) : (
              <Shield className="h-6 w-6" />
            )}
          </div>
          <div>
            <div className="flex items-center flex-wrap gap-3">
              <h3 className="font-bold text-xl text-[#333333]">{config.name}</h3>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 uppercase tracking-wider">
                {config.type.toUpperCase()}
              </span>
              {config.isActive ? (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200 uppercase tracking-wider">Active</span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600 border border-gray-200 uppercase tracking-wider">Inactive</span>
              )}
            </div>
            {config.description && (
              <p className="text-base text-[#666666] mt-2">
                {config.description}
              </p>
            )}
            <p className="text-sm text-[#999999] mt-3 font-medium">
              Created {new Date(config.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Link
            to={`/test/${config.id}`}
            className="btn btn-gold btn-sm"
          >
            <Play className="h-4 w-4 mr-1" />
            Test
          </Link>
          
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="btn btn-ghost btn-sm"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-card border border-border z-10">
                <div className="py-1">
                  <Link
                    to={`/configs/${config.id}/edit`}
                    className="flex items-center px-4 py-2 text-sm hover:bg-muted"
                    onClick={() => setShowMenu(false)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Link>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onDelete();
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-destructive hover:bg-muted"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
